import { computed, ref } from 'vue';
import axios from 'axios';
import { usePage } from '@inertiajs/vue3';
import { useLayout } from '@src/Common/Composables/useLayout';

export interface ManualTreeNode {
    uid: string;
    title: string;
    slug: string;
    children: ManualTreeNode[];
}

export interface ManualDrawerPage {
    id: number;
    uid: string;
    title: string;
    breadcrumb: Array<{ uid: string; title: string }>;
    children: Array<{ uid: string; title: string }>;
    html: string;
    is_fork: boolean;
    origin_changed: boolean;
    origin_locale: string | null;
}

export interface ManualSearchResult {
    uid: string;
    title: string;
    snippet: string | null;
}

const loading = ref(false);
const page = ref<ManualDrawerPage | null>(null);
const matched = ref(true);
const tree = ref<ManualTreeNode[]>([]);
const searchResults = ref<ManualSearchResult[] | null>(null);
const pinnedByUser = ref(false);
const lastContextKey = ref<string | null>(null);

let historyPatched = false;

/**
 * Tab switches on admin edit screens are bare history.pushState calls (no
 * Inertia visit), so patch the history API once to broadcast a window event
 * the drawer can re-match on. popstate covers back/forward.
 */
function patchHistoryOnce(): void {
    if (historyPatched || typeof window === 'undefined') {
        return;
    }

    historyPatched = true;

    for (const method of ['pushState', 'replaceState'] as const) {
        const original = window.history[method].bind(window.history);

        window.history[method] = (...args: Parameters<History['pushState']>) => {
            original(...args);
            window.dispatchEvent(new Event('manual:locationchange'));
        };
    }
}

/**
 * Singleton drawer state — survives Inertia navigation. Open/closed state
 * itself lives in useLayout('drawer.right') as the named panel id 'manual'
 * (shared drawer slot contract; 'comments' belongs to Entries).
 */
export function useManualDrawer() {
    const { change, get } = useLayout();

    const opened = computed(() => get('drawer.right') === 'manual');

    const currentRoute = (): string | null => {
        const shared = usePage().props.manual as { route?: string | null } | undefined;

        return shared?.route ?? (typeof route === 'function' ? (route().current() ?? null) : null);
    };

    // Ziggy derives params from the live location, so this stays correct after pushState tab switches.
    const currentTab = (): string | null => {
        if (typeof route !== 'function') {
            return null;
        }

        const tab = (route().params as { tab?: string | null } | undefined)?.tab;

        return typeof tab === 'string' && tab !== '' ? tab : null;
    };

    async function openForCurrentRoute(): Promise<void> {
        pinnedByUser.value = false;
        searchResults.value = null;
        loading.value = true;

        try {
            const name = currentRoute();
            const tab = currentTab();
            lastContextKey.value = name ? `${name}@${tab ?? ''}` : null;
            const match = name
                ? (await axios.get(route('manual.reader.context'), { params: { route: name, ...(tab ? { tab } : {}) } }))
                      .data?.page
                : null;

            if (match?.uid) {
                matched.value = true;
                await loadPage(match.uid as string, false);

                return;
            }

            matched.value = false;
            page.value = null;
            await loadTree();
        } finally {
            loading.value = false;
        }
    }

    async function loadPage(uid: string, pinned = true): Promise<void> {
        loading.value = true;

        try {
            const { data } = await axios.get(route('manual.reader.page', { page: uid }));
            page.value = data as ManualDrawerPage;
            searchResults.value = null;

            if (pinned) {
                pinnedByUser.value = true;
            }
        } finally {
            loading.value = false;
        }
    }

    async function loadTree(): Promise<void> {
        const { data } = await axios.get(route('manual.reader.tree'));
        tree.value = (data?.tree as ManualTreeNode[]) ?? [];
    }

    async function search(query: string): Promise<void> {
        if (!query.trim()) {
            searchResults.value = null;

            return;
        }

        const { data } = await axios.get(route('manual.reader.search'), { params: { q: query } });
        searchResults.value = (data?.results as ManualSearchResult[]) ?? [];
    }

    function open(): void {
        change('drawer.right', 'manual');
        void openForCurrentRoute();
    }

    function close(): void {
        change('drawer.right', false);
    }

    function toggle(): void {
        if (opened.value) {
            close();

            return;
        }

        open();
    }

    /** Re-match on navigation while open, unless the user navigated inside the drawer or context is unchanged. */
    function onRouteChanged(): void {
        if (!opened.value || pinnedByUser.value) {
            return;
        }

        const name = currentRoute();
        const key = name ? `${name}@${currentTab() ?? ''}` : null;

        if (key === lastContextKey.value) {
            return;
        }

        void openForCurrentRoute();
    }

    /** Start re-matching on history API navigation (pushState tab switches, back/forward). */
    function installHistoryListener(handler: () => void): void {
        patchHistoryOnce();
        window.addEventListener('manual:locationchange', handler);
        window.addEventListener('popstate', handler);
    }

    /** Stop listening to history API navigation. */
    function removeHistoryListener(handler: () => void): void {
        window.removeEventListener('manual:locationchange', handler);
        window.removeEventListener('popstate', handler);
    }

    return {
        opened,
        loading,
        page,
        matched,
        tree,
        searchResults,
        open,
        close,
        toggle,
        openForCurrentRoute,
        loadPage,
        loadTree,
        search,
        onRouteChanged,
        installHistoryListener,
        removeHistoryListener,
    };
}
